import { colors } from '../packages';
import { AppointmentStatus, S3_BUCKET_URL } from '../packages/constants/CommonConstants';
import dayjs from 'dayjs';
import moment from 'moment';
import {
  dayConstants,
  DomainTypesEnum,
  frequencyConstants,
  GLOBAL_DATE_FORMAT,
  MEDICATION_FREQUENCIES,
} from '../constants/CommonConstants';
import isYesterday from 'dayjs/plugin/isYesterday';
import { DOMAIN_ELEMENT_VARIENT } from '../pages/provider-v2/member-detail/member-detail.constents';

dayjs.extend(isYesterday);

export const parseJwt = token => {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map(c => {
        return `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`;
      })
      .join(''),
  );

  return JSON.parse(jsonPayload);
};

export function groupTimeSlotsByDate(objectsArray) {
  const dateSet = [];
  objectsArray.forEach(obj => {
    const date = dayjs(obj.start).format('YYYY-MM-DD'); // Assuming each object has a "date" property
    dateSet.push(date);
  });
  const filteredDates = dateSet.filter((value, index) => dateSet.indexOf(value) === index);
  const result = [];
  filteredDates.forEach(date => {
    const slots = objectsArray.filter(obj => dayjs(obj.start).format('YYYY-MM-DD') === date);
    result.push({ date, slots });
  });
  return result;
}

export const getDateLabel = (date, format) => {
  const tomorrow = dayjs().add(1, 'day');
  const isTomorrow = dayjs(date).isSame(tomorrow, 'day');
  return dayjs(date).isToday()
    ? 'Today'
    : isTomorrow
    ? 'Tomorrow'
    : dayjs(date).format(format ?? 'dddd, MMMM DD');
};

export const getDateLabels = (date, format) => {
  const tomorrow = dayjs().add(1, 'day');
  const isTomorrow = dayjs(date).isSame(tomorrow, 'day');
  return dayjs(date).isToday()
    ? `Today ${dayjs(date).format('h:mm a')}`
    : isTomorrow
    ? `Tomorrow ${dayjs(date).format('h:mm a')}`
    : dayjs(date).isYesterday()
    ? `Yesterday ${dayjs(date).format('h:mm a')}`
    : dayjs(date).format(format ?? 'MMMM DD, h:mm a');
};

export const getDuration = service => {
  if (service?.duration) {
    // Convert to hours and minutes
    const hours = Math.floor(service.duration / 60);
    const minutes = service.duration % 60;
    if (hours > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''} ${minutes} minutes`;
    }
    if (minutes > 0) {
      return `${minutes} minutes`;
    }
  }
  return null;
};

export const arrayUniqueByKey = (array, key) => {
  return Array.from(new Map(array.map(item => [item[key], item])).values());
};

export const getUserAuthority = token => {
  return parseJwt(token).authorities[0].replace('ROLE_', '');
};

export const formatTimeMessage = time => {
  const date = dayjs(time);
  const now = dayjs();
  const diffMinutes = now.diff(date, 'minute');
  const diffHours = now.diff(date, 'hour');
  const diffDays = now.diff(date, 'day');

  if (diffMinutes < 1) {
    return 'Just now';
  }
  if (diffMinutes < 60) {
    return `${diffMinutes} minutes ago`;
  }
  if (diffHours < 24) {
    return date.format('h:mm a');
  }
  if (diffDays < 7) {
    return date.format('dddd');
  }
  return date.format('MMM DD, YYYY');
};

// convert minutes to hours
export const convertMinutesToHours = minutes => {
  if (minutes < 60) {
    return `${minutes} minutes`;
  }

  const hours = Math.floor(minutes / 60);
  const minutesLeft = minutes % 60;
  return `${hours > 0 ? `${hours} ${hours > 1 ? 'hours' : 'hour'}` : ''} ${
    minutesLeft > 0 ? `${minutesLeft} ${minutesLeft > 1 ? 'minutes' : 'minute'}` : ''
  }`;
};

// convert second to HH:MM:SS
export const convertSecondToTime = second => {
  const hours = Math.floor(second / 3600);
  const minutes = Math.floor((second - hours * 3600) / 60);
  const seconds = second - hours * 3600 - minutes * 60;
  const mmss = `${minutes > 9 ? '' : 0}${minutes}:${seconds > 9 ? '' : 0}${seconds}`;

  if (hours) {
    return `${hours > 9 ? '' : 0}${hours}:${mmss}`;
  }
  return mmss;
};

export const isAppointmentDone = status => {
  return [AppointmentStatus.FULFILLED, AppointmentStatus.CANCELLED, AppointmentStatus.NO_SHOW].includes(
    status,
  );
};

export const convertTimeWithDate = (date, time) => {
  const hours = Math.floor(time / 100);
  const minutes = time % 100;
  const newDate = dayjs(date).set('hour', hours).set('minute', minutes).set('second', 0);
  return newDate.toISOString();
};

export const convertSlotToDate = (date, time) => {
  const hours = Math.floor(time / 100);
  const minutes = time % 100;
  const newDate = new Date(date);
  newDate.setHours(hours);
  newDate.setMinutes(minutes);
  return newDate.toString();
};

export const shortNumber = num => {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`;
  }
  return num?.toString();
};

export const getLastNDays = days => {
  const today = new Date();
  const lastNDays = [];
  for (let i = 0; i < days; i += 3) {
    const date = new Date(today.getFullYear(), today.getMonth(), today.getDate() - i);
    lastNDays.push(date.toISOString());
  }

  return lastNDays.reverse();
};

export const getDurationUnit = (duration, minimize) => {
  const minutesText = minimize ? 'min' : 'minutes';
  const hoursText = minimize ? 'hr' : 'hour';
  const hours = Math.floor(duration / 60);
  const minutes = duration % 60;
  if (hours === 0) return `${minutes} ${minutesText}`;
  if (minutes === 0) return `${hours} ${hoursText}`;
  return `${hours} ${hoursText} ${minutes} ${minutesText}`;
};

export const getCostRange = uniqueService => {
  if (uniqueService.providerServices.length === 1) {
    return `$${uniqueService.cost}`;
  }
  const minPrice = Math.min(
    ...uniqueService.providerServices.map(providerService => providerService.serviceDetails?.cost),
  );
  const maxPrice = Math.max(
    ...uniqueService.providerServices.map(providerService => providerService.serviceDetails?.cost),
  );
  if (Number.isNaN(minPrice) || Number.isNaN(maxPrice)) {
    return `$${uniqueService.cost}`;
  }
  if (minPrice === maxPrice) {
    return `$${minPrice}`;
  }
  return `$${minPrice} - $${maxPrice}`;
};

export const getLast7Days = () => {
  const today = new Date();
  const last7Days = [];
  for (let i = 0; i < 7; i += 3) {
    const date = new Date(today.getFullYear(), today.getMonth(), today.getDate() - i);
    last7Days.push(date.toISOString());
  }

  return last7Days.reverse();
};

export const getDctCBIds = (oldArr, objArr) => {
  const changedOrders = [];
  for (let i = 0; i < oldArr.length; i++) {
    const oldItem = oldArr[i];
    const objItem = objArr[i];

    if (oldItem.dctContentBlockList && objItem.dctContentBlockList) {
      const oldDctIds = oldItem.dctContentBlockList.map(item => item.cbId);
      const objDctIds = objItem.dctContentBlockList.map(item => item.cbId);

      const isDctOrderChanged = oldDctIds.some((id, index) => id !== objDctIds[index]);

      if (isDctOrderChanged) {
        changedOrders.push({
          dctId: objItem.dctId,
          cbId: objItem.cbId,
          ids: objDctIds,
        });
      }
    }
  }
  return changedOrders;
};

export const getLast30Days = () => {
  const today = new Date();
  const last30Days = [];
  for (let i = 0; i < 30; i += 3) {
    const date = new Date(today.getFullYear(), today.getMonth(), today.getDate() - i);
    last30Days.push(date.toISOString());
  }

  return last30Days.reverse();
};

export const getTodaysISOTime = () => {
  const today = new Date();
  return today.getTime();
};

export const getFullLast30Days = () => {
  const today = new Date();
  const last30Days = [];
  for (let i = 0; i < 30; i += 1) {
    const date = new Date(today.getFullYear(), today.getMonth(), today.getDate() - i);
    last30Days.push(date.toISOString());
  }

  return last30Days.reverse();
};

export const getLast90Days = () => {
  const today = new Date();
  const last90Days = [];
  for (let i = 0; i < 90; i += 3) {
    const date = new Date(today.getFullYear(), today.getMonth(), today.getDate() - i);
    last90Days.push(date.toISOString());
  }

  return last90Days.reverse();
};
export const getLast180Days = () => {
  const today = new Date();
  const last180Days = [];
  for (let i = 0; i < 180; i += 3) {
    const date = new Date(today.getFullYear(), today.getMonth(), today.getDate() - i);
    last180Days.push(date.toISOString());
  }

  return last180Days.reverse();
};

// random data in 30 days
export const getRandomData = (len = 30) => {
  const step = len === 30 ? 3 : 1;
  const data = [];

  for (let i = 0; i < len; i += step) {
    data.push(Math.floor(Math.random() * 30));
  }
  return data;
};

export const getFullImagePath = url => {
  if (url) {
    return `${S3_BUCKET_URL}${url}`;
  }
  return null;
};

export const sortingDesToAsc = (a, b) => {
  return a.createdAt - b.createdAt;
};

export const titleCase = (str = '') => {
  return str[0].toUpperCase() + str.slice(1).toLowerCase();
};

export const formatTheoryName = theory => {
  if (!theory) return '';
  return theory
    .toLowerCase()
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const createCopy = obj => {
  return JSON.parse(JSON.stringify(obj));
};

export const getReferenceId = (cbId, allBlocks, currentBlock) => {
  const blocks = allBlocks.filter(cb => cb.cbId === cbId);
  if (blocks.length > 0) {
    return blocks[0].basicInfo.referenceId;
  }
  if (currentBlock.cbId === cbId) {
    return currentBlock.basicInfo.referenceId;
  }
  return cbId;
};

export const getDisplayLogic = (cbId, contentBlocks, currentBlock) => {
  const cb = contentBlocks.filter(cblock => cblock.cbId === cbId)[0];
  if (cb && cb.rawDisplayLogic) {
    return cb.rawDisplayLogic;
  }
  if (cb && cb.displayLogics && cb.displayLogics.length > 0) {
    const { ruleAggregator } = cb;
    let logicString = '';
    cb.displayLogics.forEach((logic, index) => {
      if (logic.type === 'R') {
        logicString += `Response of Block  ${getReferenceId(logic.key, contentBlocks, currentBlock)}  `;
      } else {
        logicString += `Profile Element  ${logic.key} `;
      }
      logicString += `${logic.rule}  ${logic.value}`;
      if (index !== cb.displayLogics.length - 1) {
        logicString += ` ${ruleAggregator} `;
      }
    });
    return logicString;
  }
  return 'Always Show';
};

export const toDateString = date => (date ? dayjs(date).format(GLOBAL_DATE_FORMAT) : '');

export const diffDays = (day1, day2) => {
  const date1 = new Date(day1);
  const date2 = new Date(day2);
  const diffTime = Math.abs(date2.getTime() - date1.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

export const sharedGooglePlayload = (
  authToken,
  calenderExternalId,
  email,
  name,
  expiry,
  lastSyncAt,
  syncConfidantSystemToGoogle,
  syncGoogleToConfidantSystem,
  visibilityTag,
  status,
  type,
) => {
  const data = {
    authToken: authToken ?? '',
    calenderExternalId: calenderExternalId ?? '',
    email: email ?? '',
    name: name ?? '',
    expiry: expiry ?? '',
    lastSyncAt: lastSyncAt ?? null,
    calendarSettings: {
      syncConfidantSystemToGoogle: syncConfidantSystemToGoogle ?? true,
      syncGoogleToConfidantSystem: syncGoogleToConfidantSystem ?? true,
      visibilityTag: visibilityTag ?? '',
    },
    status: status ?? false,
    tings: {
      syncConfidantSystemToGoogle: syncConfidantSystemToGoogle ?? true,
      syncGoogleToConfidantSystem: syncGoogleToConfidantSystem ?? true,
      visibilityTag: visibilityTag ?? '',
    },
    type: type ?? '',
  };
  return data;
};

export const getDSTOffset = () => {
  return moment({ M: 1, d: 1 }).utcOffset() / 60;
};

export const getTimeByDSTOffset = time => {
  const dstOffset = getDSTOffset();
  return moment(time).utcOffset(dstOffset);
};

const formatDSTTimeStamp = date => {
  return date?.format('YYYY-MM-DDTHH:mm:ss.sssZ');
};
export const getDSTOffsetDetails = (appointmentStartTime, appointmentEndTime) => {
  let dstStartTimeStamp = '';
  let dstEndTimeStamp = '';
  if (!appointmentStartTime || !appointmentEndTime) {
    return { appointmentStartTime, appointmentEndTime };
  }
  const dateAfterDSTOffset = getTimeByDSTOffset(appointmentStartTime).utcOffset();
  const dateBeforeDSTOffset = dayjs(appointmentStartTime).utcOffset();
  if (dateAfterDSTOffset === dateBeforeDSTOffset) {
    dstStartTimeStamp = formatDSTTimeStamp(dayjs(appointmentStartTime));
    dstEndTimeStamp = formatDSTTimeStamp(dayjs(appointmentEndTime));
  } else if (dateAfterDSTOffset < dateBeforeDSTOffset) {
    dstStartTimeStamp = formatDSTTimeStamp(dayjs(appointmentStartTime).subtract(1, 'hours'));
    dstEndTimeStamp = formatDSTTimeStamp(dayjs(appointmentEndTime).subtract(1, 'hours'));
  } else {
    dstStartTimeStamp = formatDSTTimeStamp(dayjs(appointmentStartTime).add(1, 'hours'));
    dstEndTimeStamp = formatDSTTimeStamp(dayjs(appointmentEndTime).add(1, 'hours'));
  }
  return { dstStartTimeStamp, dstEndTimeStamp };
};

export const getDSTOffSetTime = timeStamp => {
  return timeStamp; // truning all the dst adjustments
  /* let dstOffSetTime = '';
  if (!timeStamp) {
    return timeStamp;
  }
  const dateAfterDSTOffset = getTimeByDSTOffset(timeStamp).utcOffset();
  const dateBeforeDSTOffset = dayjs(timeStamp).utcOffset();
  if (dateAfterDSTOffset === dateBeforeDSTOffset) {
    dstOffSetTime = formatDSTTimeStamp(dayjs(timeStamp));
  } else if (dateAfterDSTOffset < dateBeforeDSTOffset) {
    dstOffSetTime = formatDSTTimeStamp(dayjs(timeStamp).subtract(1, 'hours'));
  } else {
    dstOffSetTime = formatDSTTimeStamp(dayjs(timeStamp).add(1, 'hours'));
  }
  return dstOffSetTime; */
};

export const makeArrayUnique = (array, key) => {
  if (!Array.isArray(array)) {
    throw new Error('makeArrayUnique: The first argument must be an array.');
  }

  return array.reduce((accumulator, currentValue) => {
    // Check if the object with the same key already exists in the accumulator
    const isDuplicate = accumulator.some(item => item[key] === currentValue[key]);

    if (!isDuplicate) {
      // If it's not a duplicate, add it to the accumulator
      accumulator.push(currentValue);
    }

    return accumulator;
  }, []);
};

export const uniqueSBChannels = channels => {
  return Array.from(new Map(channels.map(channel => [channel.url, channel])).values());
};

export const uniqueConnections = connections => {
  return Array.from(new Map(connections.map(conn => [conn.connectionId, conn])).values());
};

const isRelatedTo = (key, item) => {
  return item.tagMetaData.specification[key] && item.tagMetaData.specification[key].length > 0;
};

const getRelatedToText = item => {
  let subText = null;
  if (item.tagMetaData && item.tagMetaData.specification) {
    if (isRelatedTo('relatedToMedicalCondition', item)) {
      subText = 'Related To Medical Condition';
    }
    if (isRelatedTo('relatedToMedication', item)) {
      subText = 'Related To Medication';
    }
    if (isRelatedTo('relatedToSubstanceUse', item)) {
      subText = 'Related To Substance Use';
    }
    if (isRelatedTo('relatedToWithdrawal', item)) {
      subText = 'Related To Withdrawal';
    }
  }
  return subText;
};

const getMedication = item => {
  const {
    tagMetaData: { rxDrugInfo },
  } = item;
  return `${rxDrugInfo.quantity} ${rxDrugInfo.dose}${rxDrugInfo.quantity > 1 ? 's' : ''}
      ${
        rxDrugInfo.frequency
          ? MEDICATION_FREQUENCIES.find(freq => freq.abbr === rxDrugInfo.frequency)?.display
          : ''
      }
      ${rxDrugInfo.duration ? `for ${rxDrugInfo.duration} days` : ''}`;
};

const getSubstanceDescription = (item, lookupData) => {
  const {
    tagMetaData: { substanceUse },
  } = item;
  const method = lookupData.methodsOfSubstanceUse?.find(opt => opt.name === substanceUse.methodOfUse);
  const frequency = lookupData.currentFrequencyOfSubstanceUse?.find(
    opt => opt.name === substanceUse.currentFrequencyOfUse,
  );
  const duration = lookupData.continuousLevelOfSubstanceUse?.find(
    opt => opt.name === substanceUse.howLongUsingThisLevel,
  );
  return `${method?.value || ''}
      ${frequency?.value?.toLowerCase() || ''} for a ${duration?.value?.toLowerCase() || 'days'}`;
};

export const getDomainElementDescription = (item, lookup) => {
  switch (item.domainTypeName) {
    case DomainTypesEnum.SYMPTOMS:
    case DomainTypesEnum.DIAGNOSES:
      return getRelatedToText(item);
    case DomainTypesEnum.MEDICATIONS:
      return getMedication(item);
    case DomainTypesEnum.SUBSTANCE_USE:
      return getSubstanceDescription(item, lookup);
    default:
      return '';
  }
};

export const mapToLegacy = (record, lookupData, variant = null) => {
  const legacyElement = {
    ...record,
    title: record.taxonomyName,
    date: dayjs(record.assignedAt).format('MM/DD/YYYY'),
    name: record.taxonomyName,
    medication: record.taxonomyName,
    provider: {
      fullName: record.assignedBy?.name || record.userDetails?.name,
      role: record.assignedBy?.designation || record.userDetails?.designation,
      profileImage: record.assignedBy?.profileImage,
    },
    description: getDomainElementDescription(record, lookupData),
    avatar: record.assignedBy?.profileImage,
    priority: { name: record.currentImportanceLevel },
    assignedBy: record.assignedBy?.name || record.userDetails?.name,
    notes: record.note,
    history:
      record.history?.map(historyItem =>
        mapToLegacy(
          {
            ...historyItem,
            taxonomyName: record.taxonomyName,
            currentImportanceLevel: historyItem.importanceLevel,
            importanceLevel: { name: historyItem.importanceLevel },
          },
          lookupData,
          variant,
        ),
      ) || [],
    icd: record.selectedIct10code,
    dosage:
      record.tagMetaData?.rxDrugInfo?.dose && record.tagMetaData?.rxDrugInfo?.quantity
        ? `${record.tagMetaData?.rxDrugInfo?.quantity} ${record.tagMetaData?.rxDrugInfo?.dose}${
            record.tagMetaData?.rxDrugInfo?.quantity > 1 ? 's' : ''
          }`
        : record.tagMetaData?.rxDrugInfo?.dosage,
    frequency: record.tagMetaData?.rxDrugInfo?.frequency,
    createdAt: dayjs(record.assignedAt).format('MM/DD/YYYY'),
    assignedAt: record.assignedAt,
    status: record.currentImportanceLevel,
    updatedAt: record.currentImportanceLevelDate
      ? dayjs(record.currentImportanceLevelDate).format('MM/DD/YYYY')
      : '-',
    duration: record.tagMetaData?.rxDrugInfo?.duration,
  };
  if (variant === DOMAIN_ELEMENT_VARIENT.MEDICATION) {
    legacyElement.dose = record.tagMetaData?.rxDrugInfo?.dose ?? '-';
    legacyElement.durationText = record.tagMetaData?.rxDrugInfo?.duration
      ? `${record.tagMetaData?.rxDrugInfo?.duration} day${
          record.tagMetaData?.rxDrugInfo?.duration > 1 ? 's' : ''
        }`
      : '-';
    legacyElement.drug = record.tagMetaData?.rxDrugInfo?.externalMedicationName;
    legacyElement.sig = record.tagMetaData?.rxDrugInfo?.sig;
    legacyElement.frequency = record.tagMetaData?.rxDrugInfo?.frequency;
    legacyElement.dispenseAmount = record.tagMetaData?.rxDrugInfo?.dispenseAmount;
    legacyElement.dispenseAmountText = `${
      record.tagMetaData?.rxDrugInfo?.dispenseAmount
        ? `${record.tagMetaData?.rxDrugInfo?.dispenseAmount} ${record.tagMetaData?.rxDrugInfo?.dose}${
            Number(record.tagMetaData?.rxDrugInfo?.dispenseAmount) > 1 ? 's' : ''
          }`
        : ''
    }`;
    legacyElement.quantity = record.tagMetaData?.rxDrugInfo?.quantity;
    legacyElement.pharmacyNotes = record.tagMetaData?.rxDrugInfo?.pharmacyNotes;
    legacyElement.externalMedicationId = record.tagMetaData?.rxDrugInfo?.externalMedicationId;
    legacyElement.externalMedicationName = record.tagMetaData?.rxDrugInfo?.externalMedicationName;
    legacyElement.refills = record.tagMetaData?.rxDrugInfo?.refills;
  }
  return legacyElement;
};

export const collapseObject = (obj, currentString = '') => {
  let results = {};
  if (obj) {
    Object.entries(obj).forEach(([key, value]) => {
      if (typeof value === 'object') {
        let object;
        if (currentString !== '') {
          object = collapseObject(value, `${currentString}_${key}`);
        } else {
          object = collapseObject(value, key);
        }
        results = Object.assign(results, object);
      } else if (currentString !== '') {
        results[`${currentString}_${key}`] = value;
      } else results[key] = value;
    });
  }
  return results;
};

export const toSentenceCase = (str = '') => {
  return str?.charAt(0)?.toUpperCase() + str?.slice(1)?.toLowerCase();
};

export const isUserOnline = (onlineUsers, cId) => {
  const status = onlineUsers?.includes(cId);
  return !!status;
};

export const combineMessages = (messages, timeThreshold = 60000) => {
  return messages.reduce((combinedMessages, message) => {
    const lastMessage = combinedMessages[combinedMessages.length - 1];

    if (
      lastMessage &&
      lastMessage.user.userId === message.user.userId &&
      message.createdAt - lastMessage.createdAt <= timeThreshold
    ) {
      lastMessage.message.text += `\n${message.message.text}`;
      lastMessage.createdAt = message.createdAt;
    } else {
      combinedMessages.push({ ...message });
    }

    return combinedMessages;
  }, []);
};

export const mergeConsecutiveMessages = messageList => {
  if (messageList.length === 0) return [];

  // Helper function to check if messages can be merged
  const canMergeTextMessages = (current, previous) => {
    const isSameSender = current.user.userId === previous.user.userId;
    const isTextMessage = current.type !== 'file';
    const hasNoPrevMsg =
      (!current.prevMsg?.length || !Object.keys(current?.prevMsg[0])?.length) &&
      (!previous.prevMsg?.length || !Object.keys(previous?.prevMsg[0])?.length);
    const hasNoTagMention = current.mentionedUsers.length === 0 && previous.mentionedUsers.length === 0;

    return isSameSender && isTextMessage && hasNoPrevMsg && hasNoTagMention;
  };

  // Initialize merged list with first message copy
  const mergedMessages = [];
  let currentGroup = { ...messageList[0] };

  for (let currentIndex = 1; currentIndex < messageList.length; currentIndex++) {
    const currentMessage = messageList[currentIndex];
    const isSameSender = currentMessage.user.userId === currentGroup.user.userId;

    if (isSameSender) {
      if (canMergeTextMessages(currentMessage, currentGroup)) {
        // Merge text content
        currentGroup.message.text = [currentGroup.message.text, currentMessage.message.text]
          .filter(Boolean)
          .join('<br/>');

        // Update metadata
        currentGroup.createdAt = currentMessage.createdAt;
        if (currentGroup.type === 'file') {
          currentMessage._id = currentGroup._id;
        }
      } else {
        // Finalize current group and start new group
        mergedMessages.push(currentGroup);
        currentGroup = { ...currentMessage };

        if (currentGroup.type !== 'file') {
          currentGroup._id = currentMessage._id;
        }
      }
    } else {
      mergedMessages.push(currentGroup);
      currentGroup = { ...currentMessage };
    }
  }
  mergedMessages.push(currentGroup);
  return mergedMessages;
};

export const getPaymentMethodIndicators = (insurance, demographicDetails) => {
  const glyph =
    !insurance || !insurance.patientInsuranceData?.isEnabled
      ? demographicDetails?.paymentIndicator === 'CASH'
        ? 'dollar-outlined'
        : 'info-outlined'
      : insurance.insuranceEligibilityStatus === 'complete.eligible'
      ? 'checked-circle-outlined'
      : demographicDetails?.paymentIndicator === 'CASH'
      ? 'dollar-outlined'
      : 'info-outlined';
  const text =
    demographicDetails?.preferredPaymentMethod === 'INSURANCE'
      ? 'Insurance Payments Active'
      : 'Cash Payments Active';
  const success =
    !insurance || !insurance.patientInsuranceData?.isEnabled
      ? demographicDetails?.paymentIndicator === 'CASH'
      : insurance.insuranceEligibilityStatus === 'complete.eligible'
      ? true
      : demographicDetails?.paymentIndicator === 'CASH';
  const color = success ? colors.green500 : colors.destructive500;
  return { glyph, text, success, color };
};

export const getTimeForEval = duration => {
  if (!duration) {
    return '0 min';
  }
  const totalSeconds = duration * 60;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  // If seconds are zero, just return minutes.
  if (seconds === 0) {
    return `${minutes} min${minutes > 1 ? 's' : ''}`;
  }
  return `${minutes} min ${seconds} sec`;
};
